// LowStockTable.jsx
import React from "react";
import { X, AlertTriangle, Search, Package } from "lucide-react";

const LowStockTable = ({ lowStockItems, onClose }) => {
  const [searchTerm, setSearchTerm] = React.useState("")
  const [entriesPerPage, setEntriesPerPage] = React.useState(10)

  const filteredItems = lowStockItems.filter((item) =>
    item.productName.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const displayedItems = filteredItems.slice(0, entriesPerPage)

  const styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modal: {
      backgroundColor: "white",
      borderRadius: "12px",
      padding: "24px",
      width: "90%",
      maxWidth: "800px",
      maxHeight: "80vh",
      overflow: "auto",
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "20px",
      paddingBottom: "16px",
      borderBottom: "2px solid #e2e8f0",
    },
    title: {
      fontSize: "1.5rem",
      fontWeight: "700",
      color: "#dc3545",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    closeButton: {
      padding: "8px",
      backgroundColor: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      fontSize: "1.2rem",
      fontWeight: "bold",
    },
    controls: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
      gap: "12px",
    },
    searchContainer: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    select: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
    },
    searchInput: {
      padding: "8px 12px",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "14px",
      width: "200px",
    },
    searchButton: {
      padding: "8px 12px",
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "6px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      overflow: "hidden",
    },
    th: {
      backgroundColor: "#f8f9fa",
      padding: "12px",
      textAlign: "left",
      fontWeight: "600",
      color: "#374151",
      borderBottom: "2px solid #e2e8f0",
      fontSize: "14px",
    },
    td: {
      padding: "12px",
      borderBottom: "1px solid #e2e8f0",
      fontSize: "14px",
      color: "#374151",
    },
    lowStockBadge: {
      backgroundColor: "#fee2e2",
      color: "#dc2626",
      padding: "4px 8px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "500",
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
    },
    footer: {
      marginTop: "16px",
      fontSize: "12px",
      color: "#6b7280",
    },
    emptyState: {
      textAlign: "center",
      padding: "40px 20px",
      color: "#6b7280",
    },
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            <AlertTriangle size={24} />
            Low Stock Items
          </h2>
          <button onClick={onClose} style={styles.closeButton}>
            ×
          </button>
        </div>

        <div style={styles.controls}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>Show</span>
            <select
              value={entriesPerPage}
              onChange={(e) => setEntriesPerPage(Number.parseInt(e.target.value))}
              style={styles.select}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span style={{ fontSize: "14px", color: "#6b7280" }}>entries</span>
          </div>

          <div style={styles.searchContainer}>
            <input
              type="text"
              placeholder="Search for items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button style={styles.searchButton}>
              <Search size={16} />
            </button>
          </div>
        </div>

        {displayedItems.length > 0 ? (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>S.No</th>
                  <th style={styles.th}>Product Name</th>
                  <th style={styles.th}>Current Stock</th>
                  <th style={styles.th}>Alert Level</th>
                  <th style={styles.th}>Price (₹)</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.map((item, index) => (
                  <tr key={item.id}>
                    <td style={styles.td}>{index + 1}</td>
                    <td style={styles.td}>{item.productName}</td>
                    <td style={styles.td}>{Number.parseInt(item.openingStock) || 0}</td>
                    <td style={styles.td}>{Number.parseInt(item.lowStockAlert) || 0}</td>
                    <td style={styles.td}>{Number.parseFloat(item.sellingRate || 0).toFixed(2)}</td>
                    <td style={styles.td}>
                      <span style={styles.lowStockBadge}>
                        <AlertTriangle size={12} />
                        Low Stock
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={styles.footer}>
              Showing {displayedItems.length} of {filteredItems.length} entries
              {searchTerm && ` (filtered from ${lowStockItems.length} total entries)`}
            </div>
          </>
        ) : (
          <div style={styles.emptyState}>
            <Package size={48} style={{ marginBottom: "16px", opacity: 0.5 }} />
            <p>No low stock items found</p>
            {searchTerm && <p style={{ fontSize: "12px", marginTop: "8px" }}>Try adjusting your search term</p>}
          </div>
        )}
      </div>
    </div>
  )
}

export default LowStockTable;